/**
 * Turning the Activity tab's flat change feed into per-slide version ladders.
 *
 * Six edits to one slide are not six peers with six independent undos — they
 * are a stack, and undoing an older one rewinds past the newer ones (the
 * server's `revertChain` does exactly that). Presenting them as peers is what
 * made a busy slide unreadable and its Revert buttons untrustworthy (Jake,
 * 2026-07-31). Pure data-in/data-out so it can be tested without the panel.
 */

import type { ActivityRow } from '../../shared/api.ts';

/** A slide with one settled change, or any row that has no slide at all. */
export type ActivitySingle = { kind: 'single'; key: string; row: ActivityRow };

/** A slide's version ladder. `redoId`, `changeCount` and `minShown` come from
 *  `ladderRoles` — see there for what each one is protecting. */
export type ActivityStack = {
  kind: 'stack';
  key: string;
  slideId: string;
  rows: ActivityRow[];
} & LadderRoles;

export type ActivityItem = ActivitySingle | ActivityStack;

/**
 * Settled rows (already newest-first) → an ordered list of stacks and singles.
 *
 * One stack per slide, positioned by its newest change, so the feed still reads
 * chronologically. A slide with a single settled change stays a plain row —
 * stack chrome around one item is noise. Rows with no slide (a reorder, a run
 * error on deck scope) are always singles: there is no ladder to draw.
 */
export function groupSettled(rows: ActivityRow[]): ActivityItem[] {
  // A stack is built without its roles and gains them in the map below, so the
  // accumulator holds the partial form rather than the finished one.
  type PartialStack = { kind: 'stack'; key: string; slideId: string; rows: ActivityRow[] };
  const items: (ActivitySingle | PartialStack)[] = [];
  const bySlide = new Map<string, PartialStack>();
  for (const row of rows) {
    const id = row.slideId;
    if (!id) {
      items.push({ kind: 'single', key: `row:${row.toolId || row.historyId}`, row });
      continue;
    }
    let stack = bySlide.get(id);
    if (!stack) {
      stack = { kind: 'stack', key: `slide:${id}`, slideId: id, rows: [] };
      bySlide.set(id, stack);
      items.push(stack);
    }
    stack.rows.push(row);
  }
  return items.map((item): ActivityItem => {
    if (item.kind !== 'stack') return item;
    // Reading rows[0] through a binding rather than an index: length === 1 is
    // not something the checker carries across the two expressions.
    const only = item.rows.length === 1 ? item.rows[0] : undefined;
    if (only) return { kind: 'single', key: item.key, row: only };
    return { ...item, ...ladderRoles(item.rows) };
  });
}

export const rowId = (row: ActivityRow): string => row.historyId || row.toolId;

/**
 * rowId → how many changes undoing that row takes off its slide (newest = 1).
 *
 * Counted over EVERY row the deck has, not just the settled ones, because a row
 * under review moves out of its stack into "Awaiting review" — and if it were
 * dropped from the count the steps below it would renumber and promise to undo
 * fewer changes than they do. It is also what lets the reviewing row itself say
 * how far back it goes, which is the one moment the user needs to know.
 *
 * `rows` arrives newest-first deck-wide, so a running per-slide tally is the
 * rank.
 */
export function undoCountsByRow(rows: ActivityRow[]): Map<string, number> {
  const seen = new Map<string, number>();
  const counts = new Map<string, number>();
  for (const row of rows) {
    if (!row.slideId || row.status !== 'applied' || !row.revertible) continue;
    const n = (seen.get(row.slideId) || 0) + 1;
    seen.set(row.slideId, n);
    counts.set(rowId(row), n);
  }
  return counts;
}

/** slideId → the row whose output the slide is currently showing (its newest
 *  applied change), across every row. A stack marks "now" only when that row is
 *  one of its own. A slide under review has no answer at all: what is on the
 *  canvas is the preview, so pointing at a step would be wrong until the
 *  review settles. */
export function currentBySlide(rows: ActivityRow[]): Map<string, string> {
  const reviewing = new Set(rows.filter((r) => r.previewing && r.slideId).map((r) => r.slideId));
  const current = new Map<string, string>();
  for (const row of rows) {
    if (!row.slideId || row.status !== 'applied' || !row.revertible) continue;
    if (reviewing.has(row.slideId)) continue;
    if (!current.has(row.slideId)) current.set(row.slideId, rowId(row));
  }
  return current;
}

/**
 * Who can do what within one slide's stack. Two rules, both consequences of the
 * history being a ladder rather than a set (how far back each step rewinds is
 * `undoCountsByRow`, which has to see the whole deck):
 *
 *  - `redoId` — only the OLDEST undone change can be put back: the slide holds
 *    ITS pre-change source, so it is the only redo that lands on the state it
 *    was recorded against. Redo in order and the ladder walks back up.
 *  - `minShown` — folding older changes away must never hide something still
 *    actionable, so the collapsed view runs down to the last such row: the redo
 *    candidate, and any leftover (a change nobody chose, whose row is the
 *    user's only handle on it).
 */
export type LadderRoles = {
  /** The one row that can be put back, or null. */
  redoId: string | null;
  changeCount: number;
  /** How many rows the collapsed view must keep on screen. */
  minShown: number;
};

export function ladderRoles(rows: ActivityRow[]): LadderRoles {
  const applied = rows.filter((r) => r.status === 'applied' && r.revertible);
  const newestApplied = applied[0];
  const newestAppliedTs = newestApplied ? newestApplied.ts : -Infinity;
  // Undoing the create that made this slide takes the slide out of the deck,
  // and a create cannot be put back — so nothing above it can be redone either.
  // Offering Redo there would just 404 on a slide that no longer exists.
  const slideRemoved = rows.some((r) => r.tool === 'create_slide' && r.status === 'reverted');
  const redoable = slideRemoved ? [] : rows.filter((r) => r.reapplyable && r.ts > newestAppliedTs);
  const oldestRedoable = redoable[redoable.length - 1] ?? null;
  const lastActionable = rows.reduce((max, r, i) => (r === oldestRedoable || r.leftover ? i : max), 0);
  return {
    redoId: oldestRedoable ? rowId(oldestRedoable) : null,
    changeCount: rows.filter((r) => r.tool !== 'turn_error' && r.tool !== 'turn_stopped').length,
    minShown: lastActionable + 1,
  };
}
